import TableSkeleton from "@/app/components/ui/Skeletons/TableSkeleton";
import { useLoader } from "@/app/hooks/useLoader";
import useRouteInfo from "@/app/hooks/useRouteInfo";
import { useRouter } from "@/app/hooks/useRouter";
import { ICalendarState } from "@/app_redux/reducers/slices/auth/staff_calendar_slice";
import clsx from "clsx";
import { chunk } from "lodash";
import { useEffect, useRef, useState } from "react";
import CalendarGridViewComponent from "../CalendarGridViewComponent";
import CalendarTodaysViewComponent from "../CalendarTodaysViewComponent";
import CalendarViewToggleComponent from "../CalendarViewToggleComponent";
import HeaderComponent from "../HeaderComponent";
import StaffCalendarSlideButtons from "../StaffCalendarSlideButtons";
import StaffCalendarQuickFilters from "../StaffCalendarQuickFilters";
import CalendarViewFilterComponent from "../CalendarFilterComponent";
import SimpleBadge from "@/app/components/ui/Badges/SimpleBadge";
import ToggleWithIcon from "@/app/components/ui/Toggles/ToggleWithIcon";
import { BlockPositionType, ReducedStaffDataType } from "../interface";

const CALENDAR_DATA_CHUNK_SIZE = 6;

function CalendarViewComponent() {
  const { getRouteKey } = useRouter();

  const isCalendarLoading = useLoader("isStaffCalendarLoadingInProgress");
  const [slideIndex, setSlideIndex] = useState<number>(0);
  const [showFilters, setShowFilters] = useState<boolean>(false);
  const { routeState } = useRouteInfo(getRouteKey("STAFF_CALENDAR", "id"));
  const { calendarData, gridView, expandedView } = routeState.state as ICalendarState;
  const [reducedStaffData, setReducedStaffData] = useState<ReducedStaffDataType | null>(null);
  const [overlappedBlocks, setOverlappedBlocks] = useState<Array<BlockPositionType[]>>([]);
  const stickyHeaderRef = useRef<HTMLDivElement | null>(null);
  const [stickyHeaderHeight, setStickyHeaderHeight] = useState<number>(0);

  useEffect(() => {
    const node = stickyHeaderRef.current;
    if (!node) return;

    const updateStickyHeaderHeight = () => {
      setStickyHeaderHeight(node.getBoundingClientRect().height);
    };

    updateStickyHeaderHeight();

    if (typeof ResizeObserver !== "undefined") {
      const resizeObserver = new ResizeObserver(updateStickyHeaderHeight);
      resizeObserver.observe(node);
      return () => resizeObserver.disconnect();
    }

    window.addEventListener("resize", updateStickyHeaderHeight);
    return () => window.removeEventListener("resize", updateStickyHeaderHeight);
  }, []);

  return (
    <div
      className={clsx(
        "bg-white transition-transform",
        expandedView
          ? "fixed inset-0 z-40 h-[100dvh] w-full overflow-y-auto px-3 sm:px-6 lg:px-10"
          : "relative min-h-[600px] md:min-h-[1000px] overflow-visible",
      )}
    >
      <div ref={stickyHeaderRef} className=" top-0 w-full sticky z-30   bg-white pt-1 pb-1">
        <HeaderComponent />
        <div className="flex flex-col gap-2 md:flex-row md:items-center md:justify-between">
          <div className="flex w-full flex-col items-stretch gap-2 md:w-auto md:items-start">
            <div className="flex justify-start items-center gap-x-2 md:hidden">
              <p className="text-sm">Show Filters</p>
              <ToggleWithIcon
                toggleState={showFilters}
                onToggleChange={(status) => {
                  setShowFilters(status);
                }}
              />
            </div>
            <div id="staff-calendar-filters" className={clsx(showFilters ? "block" : "hidden", "md:block")}>
              <CalendarViewFilterComponent setSlideIndex={setSlideIndex} />
            </div>
          </div>

          <div className="flex w-full justify-between items-center md:w-auto md:justify-end">
            <div className="flex justify-start items-center gap-x-2">
              <CalendarViewToggleComponent />
              <StaffCalendarSlideButtons
                maxLength={chunk(calendarData, CALENDAR_DATA_CHUNK_SIZE).length}
                setSlideIndex={setSlideIndex}
                slideIndex={slideIndex}
              />
            </div>
          </div>
        </div>
        <div className="mt-1 flex flex-col gap-2 rounded-xl border border-gray-200 bg-[#f8fafc] px-2 py-2 sm:px-3 md:flex-row md:items-center md:justify-between md:gap-3">
          <div className="flex flex-wrap items-center gap-x-4 gap-y-1 md:gap-5">
            <div className="flex items-center gap-2">
              <span className="text-[10px] font-semibold uppercase tracking-wide text-gray-600 sm:text-[11px]">
                Overlapped
              </span>
              <SimpleBadge label={`${overlappedBlocks?.flat()?.length}`} />
            </div>
            <div className="hidden h-4 w-px bg-gray-300 md:block"></div>
            <div className="flex items-center gap-2">
              <span className="text-[10px] font-semibold uppercase tracking-wide text-gray-600 sm:text-[11px]">
                Total Bookings
              </span>
              <SimpleBadge label={`${Object.values(reducedStaffData ?? {})?.flat()?.length}`} />
            </div>
          </div>
          <div className="min-w-0 md:flex md:justify-end">
            <StaffCalendarQuickFilters />
          </div>
        </div>
      </div>

      {!isCalendarLoading ? (
        !gridView ? (
          <CalendarTodaysViewComponent
            expandedView={expandedView}
            calendarData={calendarData}
            slideIndex={slideIndex}
            stickyTopOffset={stickyHeaderHeight}
            setReducedStaffData={setReducedStaffData}
            reducedStaffData={reducedStaffData}
            setOverlappedBlocks={setOverlappedBlocks}
            overlappedBlocks={overlappedBlocks}
          />
        ) : (
          <CalendarGridViewComponent />
        )
      ) : (
        <TableSkeleton />
      )}
    </div>
  );
}

export default CalendarViewComponent;
